import * as HPKE from "hpke";

export interface HpkePublicKey {
  key: HPKE.Key;
  fingerprint: string;
}

export const COMMITMENT_CONTEXT = new TextEncoder().encode(
  "credential-commitment"
);
export const COMMITMENT_LEN = 32;
export const FINGERPRINT_BYTES = 8;

export const suite = new HPKE.CipherSuite(
  HPKE.KEM_DHKEM_P256_HKDF_SHA256,
  HPKE.KDF_HKDF_SHA256,
  HPKE.AEAD_AES_256_GCM
);

export function toBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++)
    binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

export function fromBase64(str: string): Uint8Array {
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function computeFingerprint(data: Uint8Array): Promise<string> {
  const hash = new Uint8Array(
    await crypto.subtle.digest("SHA-256", Uint8Array.from(data))
  );
  return Array.from(hash.slice(0, FINGERPRINT_BYTES))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/** Parse a base64 raw P-256 public key (65 bytes, uncompressed). */
export async function parsePublicKey(b64: string): Promise<HpkePublicKey> {
  const key = await suite.DeserializePublicKey(fromBase64(b64.trim()));
  const serialized = await suite.SerializePublicKey(key);
  const fingerprint = await computeFingerprint(serialized);
  return { key, fingerprint };
}

/** The base64 raw P-256 encoding of a public key — the round-trip of
 * `parsePublicKey`, suitable for publishing to a client that will re-parse and
 * seal against it. */
export async function serializePublicKeyBase64(
  publicKey: HpkePublicKey
): Promise<string> {
  return toBase64(await suite.SerializePublicKey(publicKey.key));
}

/**
 * Encrypt a string to an HPKE recipient and format the `$hpke$1$…` envelope.
 * This is the single definition of the on-the-wire ciphertext contract; both
 * the UI (encryptSecret.ts) and the server (crypto.ts) seal through here so the
 * format and commitment derivation cannot drift between them.
 */
export async function hpkeSeal(
  plaintext: string,
  publicKey: HpkePublicKey
): Promise<string> {
  const { encapsulatedSecret, ctx } = await suite.SetupSender(publicKey.key);
  const ct = await ctx.Seal(new TextEncoder().encode(plaintext));
  const commitment = await ctx.Export(COMMITMENT_CONTEXT, COMMITMENT_LEN);
  return `$hpke$1$${publicKey.fingerprint}$${toBase64(encapsulatedSecret)}$${toBase64(ct)}$${toBase64(commitment)}`;
}

export interface HpkeEnvelope {
  fingerprint: string;
  enc: Uint8Array;
  ct: Uint8Array;
  commitment: Uint8Array;
}

/** Parse an `$hpke$1$…` envelope into its fields. */
export function parseHpkeEnvelope(ciphertext: string): HpkeEnvelope {
  const parts = ciphertext.split("$");
  // ["", "hpke", "1", fingerprint, enc, ct, commitment]
  if (parts.length !== 7) throw new Error("Invalid HPKE ciphertext format");
  const [, , , fp, encB64, ctB64, commitB64] = parts;
  return {
    fingerprint: fp!,
    enc: fromBase64(encB64!),
    ct: fromBase64(ctB64!),
    commitment: fromBase64(commitB64!),
  };
}
